import { useCallback, useEffect, useMemo, useState } from 'react';
import { OnboardingStepId, ONBOARDING_STEPS } from '../../types/onboarding';
import { onboardingStorage } from '../../services/Onboarding';
import { profileService } from '../../services/Profile/profileService';
import { logger } from '../../utils/logger';

// Which stored fields each step is responsible for, keyed by the completeness
// item keys the backend returns (see ProfileService.getCompleteness). A step
// listed here counts as complete only when its data actually exists on the
// server — not merely because the user clicked past it.
//
// Steps absent from this map (welcome, persona-confirmation, app-tour) collect
// nothing persistent, so advancing past them IS completing them.
const STEP_DATA_REQUIREMENTS: Partial<Record<OnboardingStepId, string[]>> = {
  assessment: ['persona'],
  photos: ['photos'],
  profile: ['location', 'bio'],
  interests: ['interests'],
  preferences: ['preferences', 'intent'],
};

export const useOnboardingProgress = () => {
  const progress = onboardingStorage.getProgress();

  // Server-side truth about which fields are filled in. Without this the bar
  // is a per-device tally of button presses: it reads 0% on a new device for a
  // profile that is actually finished, and 100% for one that is empty.
  const [satisfiedKeys, setSatisfiedKeys] = useState<Set<string> | null>(null);
  // The weighted completion score, straight from the backend. This is the ONE
  // number shown anywhere completion is reported — the onboarding bar, the
  // completion screen, and the Matches feed prompt all read it, so they cannot
  // disagree the way a locally-derived step count did.
  const [score, setScore] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const completeness = await profileService.getCompleteness();
      setSatisfiedKeys(new Set(completeness.completed));
      setScore(completeness.score);
    } catch (err) {
      logger.log('[ONBOARDING] Could not read stored completeness:', err);
    }
  }, []);

  // Re-read whenever the user moves through the flow, so the bar advances as
  // each step's data lands on the server.
  useEffect(() => {
    refresh();
  }, [refresh, progress?.currentStep]);

  const progressData = useMemo(() => {
    // The "completion" step is the summary screen itself — it's never added
    // to completedSteps/skippedSteps (the user leaves it via "Start
    // Exploring", not "Next"/"Skip"), so it must be excluded from the
    // denominator or the count can never reach 100%.
    const trackableSteps = ONBOARDING_STEPS.filter(step => step.id !== 'completion');
    const totalSteps = trackableSteps.length;

    const skipped = new Set(
      (progress?.skippedSteps || []).filter(id => trackableSteps.some(step => step.id === id))
    );
    const locallyCompleted = progress?.completedSteps || [];

    // A step is complete when the thing it exists to collect is actually
    // stored. Skipping no longer counts: a skipped step is work the user has
    // explicitly declined, and filling the bar for it was what let the meter
    // read 100% over an empty profile.
    const isStepComplete = (stepId: OnboardingStepId): boolean => {
      const required = STEP_DATA_REQUIREMENTS[stepId];

      if (!required) {
        // Informational step — advancing past it (not skipping) completes it.
        return locallyCompleted.includes(stepId);
      }

      // Data-backed step. Until the server responds, fall back to the local
      // flag so the bar isn't briefly empty on first paint.
      if (!satisfiedKeys) return locallyCompleted.includes(stepId);

      return required.every(key => satisfiedKeys.has(key));
    };

    const completedStepIds = trackableSteps.map(s => s.id).filter(isStepComplete);
    const completedCount = completedStepIds.length;
    const skippedCount = Array.from(skipped).filter(id => !completedStepIds.includes(id)).length;

    // Weighted server score when we have it. The step-count ratio is only a
    // placeholder for the moment before the first fetch resolves — the two are
    // NOT interchangeable (the weighted score values a persona far above an
    // app tour), which is precisely why reporting one in the flow and the
    // other in the feed produced two different "% complete" numbers.
    const stepRatio = Math.min(100, Math.round((completedCount / totalSteps) * 100));
    const percentage = score ?? stepRatio;

    const stepStatuses = ONBOARDING_STEPS.reduce((acc, step) => {
      const complete = step.id === 'completion' ? false : isStepComplete(step.id);
      acc[step.id] = {
        isComplete: complete,
        // Shown as skipped only while it remains genuinely unfilled — a step
        // skipped earlier but satisfied later reads as complete.
        isSkipped: skipped.has(step.id) && !complete,
        isCurrent: progress?.currentStep === step.id
      };
      return acc;
    }, {} as Record<OnboardingStepId, { isComplete: boolean; isSkipped: boolean; isCurrent: boolean }>);

    return {
      totalSteps,
      completedCount,
      skippedCount,
      totalCompleted: completedCount,
      percentage,
      stepStatuses,
      // Clamped: the index is found across all steps including 'completion',
      // while totalSteps excludes it — unclamped, the summary screen rendered
      // as "Step 9 of 8".
      currentStepIndex: Math.min(
        totalSteps,
        Math.max(1, ONBOARDING_STEPS.findIndex(step => step.id === progress?.currentStep) + 1)
      ),
      refresh,
    };
  }, [progress, satisfiedKeys, score, refresh]);

  return progressData;
};
